'use client';

import { useState, useEffect } from 'react';
import ResourceCard from './ResourceCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import WebinarCard from './WebinarCard';
import VideoTutorialCard from './VideoTutorialCard';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  title: string;
  description: string;
  category: string;
  image: string;
  alt: string;
  downloadUrl: string;
  fileSize: string;
  fileType: string;
  downloads: number;
}

interface Webinar {
  id: number;
  title: string;
  description: string;
  speaker: string;
  speakerTitle: string;
  date: string;
  duration: string;
  image: string;
  alt: string;
  registrationUrl: string;
  attendees: number;
}

interface VideoTutorial {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  alt: string;
  duration: string;
  views: number;
  category: string;
  videoUrl: string;
}

const mockResources: Resource[] = [
{
  id: 1,
  title: "C-Arm Installation Guide: Complete Setup Protocol",
  description: "Comprehensive step-by-step installation procedures for mobile C-Arm systems including electrical requirements, radiation safety protocols, and quality assurance testing procedures.",
  category: "Installation Guides",
  image: "https://images.unsplash.com/photo-1559000357-f6b52ddfbe37",
  alt: "Medical technician installing C-Arm imaging equipment in modern surgical suite",
  downloadUrl: "/downloads/c-arm-installation.pdf",
  fileSize: "4.2 MB",
  fileType: "PDF",
  downloads: 2847
},
{
  id: 2,
  title: "FDA Compliance Checklist for Medical Imaging Equipment",
  description: "Essential regulatory compliance documentation covering FDA 510(k) requirements, quality system regulations, and medical device reporting obligations for imaging equipment distributors.",
  category: "Regulatory Updates",
  image: "https://images.unsplash.com/photo-1717697675533-eac9223553c2",
  alt: "Compliance documents and FDA certification papers on desk with medical equipment in background",
  downloadUrl: "/downloads/fda-compliance.pdf",
  fileSize: "2.8 MB",
  fileType: "PDF",
  downloads: 3921
},
{
  id: 3,
  title: "Preventive Maintenance Schedule: Surgical Equipment",
  description: "Detailed maintenance protocols for surgical tables, lights, and imaging systems including daily checks, monthly inspections, and annual certification requirements with troubleshooting guides.",
  category: "Maintenance Protocols",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1324d53-1764604845342.png",
  alt: "Biomedical engineer performing preventive maintenance on surgical equipment with diagnostic tools",
  downloadUrl: "/downloads/maintenance-schedule.pdf",
  fileSize: "3.5 MB",
  fileType: "PDF",
  downloads: 1876
},
{
  id: 4,
  title: "Medical Imaging Technology Trends 2025",
  description: "Industry analysis covering emerging technologies in medical imaging including AI integration, portable imaging solutions, and next-generation detector technologies with market forecasts.",
  category: "Industry Insights",
  image: "https://images.unsplash.com/photo-1559000357-f6b52ddfbe37",
  alt: "Modern medical imaging equipment display showing advanced technology and digital interfaces",
  downloadUrl: "/downloads/tech-trends-2025.pdf",
  fileSize: "5.1 MB",
  fileType: "PDF",
  downloads: 4532
},
{
  id: 5,
  title: "Equipment Comparison: Mobile vs Fixed C-Arms",
  description: "Technical comparison guide analyzing mobile and fixed C-Arm systems covering image quality, radiation dose, operational flexibility, and total cost of ownership considerations.",
  category: "Technical Resources",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18f187e69-1764604844087.png",
  alt: "Side-by-side comparison of mobile and fixed C-Arm imaging systems in hospital setting",
  downloadUrl: "/downloads/c-arm-comparison.pdf",
  fileSize: "3.9 MB",
  fileType: "PDF",
  downloads: 2134
},
{
  id: 6,
  title: "CE Marking Requirements for Medical Devices",
  description: "Complete guide to European medical device regulations covering CE marking procedures, technical documentation requirements, and conformity assessment pathways for international distribution.",
  category: "Regulatory Updates",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_102512184-1764604839787.png",
  alt: "CE marking certification documents with European Union medical device directive papers",
  downloadUrl: "/downloads/ce-marking-guide.pdf",
  fileSize: "2.6 MB",
  fileType: "PDF",
  downloads: 1654
},
{
  id: 7,
  title: "Radiation Safety Protocols for Imaging Equipment",
  description: "Comprehensive radiation safety guidelines covering ALARA principles, shielding requirements, dosimetry monitoring, and staff training protocols for medical imaging facilities.",
  category: "Technical Resources",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9b0af8a-1764604843598.png",
  alt: "Radiation safety equipment and dosimetry badges displayed with protective shielding materials",
  downloadUrl: "/downloads/radiation-safety.pdf",
  fileSize: "4.7 MB",
  fileType: "PDF",
  downloads: 3287
},
{
  id: 8,
  title: "Troubleshooting Guide: Common Imaging Equipment Issues",
  description: "Practical troubleshooting manual addressing frequent technical issues in medical imaging equipment with diagnostic flowcharts, error code explanations, and resolution procedures.",
  category: "Maintenance Protocols",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d105dda-1764604846410.png",
  alt: "Biomedical technician troubleshooting medical imaging equipment with diagnostic computer screen",
  downloadUrl: "/downloads/troubleshooting-guide.pdf",
  fileSize: "3.2 MB",
  fileType: "PDF",
  downloads: 2765
}];


const mockWebinars: Webinar[] = [
{
  id: 1,
  title: "Advanced C-Arm Techniques for Interventional Procedures",
  description: "Expert-led session covering advanced imaging techniques, dose optimization strategies, and workflow efficiency improvements for interventional radiology and surgical applications.",
  speaker: "Dr. Michael Chen",
  speakerTitle: "Chief of Interventional Radiology",
  date: "December 15, 2025",
  duration: "90 minutes",
  image: "https://images.unsplash.com/photo-1575029644286-efb9039cac46",
  alt: "Medical professional presenting C-Arm imaging techniques on large screen in conference room",
  registrationUrl: "/webinars/c-arm-techniques",
  attendees: 342
},
{
  id: 2,
  title: "Regulatory Compliance Updates: FDA & CE Requirements",
  description: "Comprehensive overview of latest regulatory changes affecting medical equipment distribution including updated FDA guidance documents and European MDR implementation timelines.",
  speaker: "Sarah Martinez",
  speakerTitle: "Regulatory Affairs Director",
  date: "December 20, 2025",
  duration: "60 minutes",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f10e1123-1764604845616.png",
  alt: "Regulatory compliance expert presenting FDA and CE marking requirements to healthcare professionals",
  registrationUrl: "/webinars/regulatory-updates",
  attendees: 287
},
{
  id: 3,
  title: "Preventive Maintenance Best Practices for Surgical Equipment",
  description: "Hands-on training session demonstrating effective preventive maintenance strategies, documentation requirements, and cost-saving approaches for surgical equipment management.",
  speaker: "James Thompson",
  speakerTitle: "Senior Biomedical Engineer",
  date: "January 10, 2026",
  duration: "75 minutes",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_187108bf2-1764604844404.png",
  alt: "Biomedical engineer demonstrating preventive maintenance procedures on surgical equipment to group",
  registrationUrl: "/webinars/maintenance-practices",
  attendees: 198
}];


const mockVideoTutorials: VideoTutorial[] = [
{
  id: 1,
  title: "C-Arm System Setup: Step-by-Step Installation",
  description: "Complete video walkthrough of mobile C-Arm installation from unpacking to final quality assurance testing with safety protocol demonstrations.",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_14e63051f-1764604843602.png",
  alt: "Video tutorial showing biomedical technician installing mobile C-Arm system in operating room",
  duration: "24:35",
  views: 8742,
  category: "Installation",
  videoUrl: "/videos/c-arm-setup"
},
{
  id: 2,
  title: "Radiation Safety Training for Medical Staff",
  description: "Essential radiation safety training covering ALARA principles, proper shielding techniques, and dosimetry monitoring for healthcare professionals.",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17abb97ff-1764604845804.png",
  alt: "Radiation safety instructor demonstrating proper protective equipment use in medical imaging environment",
  duration: "18:42",
  views: 12456,
  category: "Safety",
  videoUrl: "/videos/radiation-safety"
},
{
  id: 3,
  title: "Troubleshooting Common Imaging Equipment Errors",
  description: "Practical demonstration of diagnosing and resolving frequent technical issues in medical imaging equipment with expert troubleshooting techniques.",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d516bcf5-1764604843454.png",
  alt: "Technician demonstrating troubleshooting procedures on medical imaging equipment control panel",
  duration: "31:18",
  views: 6891,
  category: "Maintenance",
  videoUrl: "/videos/troubleshooting"
},
{
  id: 4,
  title: "Quality Assurance Testing for Surgical Equipment",
  description: "Comprehensive guide to performing quality assurance tests on surgical equipment including calibration procedures and documentation requirements.",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7d509d9-1764604844123.png",
  alt: "Quality assurance specialist performing calibration tests on surgical imaging equipment",
  duration: "27:54",
  views: 5234,
  category: "Quality Control",
  videoUrl: "/videos/qa-testing"
}];


const categories = [
"All Resources",
"Installation Guides",
"Maintenance Protocols",
"Regulatory Updates",
"Industry Insights",
"Technical Resources"];


export default function ResourcesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All Resources");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredResources, setFilteredResources] = useState(mockResources);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = mockResources;

    if (activeCategory !== "All Resources") {
      filtered = filtered.filter((resource) => resource.category === activeCategory);
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter((resource) =>
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredResources(filtered);
  }, [activeCategory, searchQuery, isHydrated]);

  const handleCategoryChange = (category: string) => {
    if (!isHydrated) return;
    setActiveCategory(category);
  };

  const handleSearch = (query: string) => {
    if (!isHydrated) return;
    setSearchQuery(query);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full px-4 lg:px-8 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="h-96 flex items-center justify-center">
              <div className="animate-pulse text-text-secondary">Loading resources...</div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Knowledge Hub & Resource Center
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Access comprehensive technical documentation, installation guides, maintenance protocols, and industry insights to support your medical equipment operations.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                <Icon name="DocumentTextIcon" size={20} />
                <span className="font-medium">500+ Technical Documents</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                <Icon name="VideoCameraIcon" size={20} />
                <span className="font-medium">100+ Video Tutorials</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                <Icon name="AcademicCapIcon" size={20} />
                <span className="font-medium">Monthly Webinars</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
            <SearchBar
              onSearch={handleSearch}
              placeholder="Search technical resources, guides, protocols..." />

          </div>
          
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange} />

        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-text-primary">
              Technical Resources & Documentation
            </h2>
            <span className="text-sm text-text-secondary">
              {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'} found
            </span>
          </div>

          {filteredResources.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredResources.map((resource) =>
            <ResourceCard key={resource.id} {...resource} />
            )}
            </div> :

          <div className="text-center py-16">
              <Icon name="DocumentMagnifyingGlassIcon" size={64} className="mx-auto text-text-secondary mb-4" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">No resources found</h3>
              <p className="text-text-secondary">Try adjusting your search or filter criteria</p>
            </div>
          }
        </div>
      </section>

      {/* Video Tutorials Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Video Tutorial Library
              </h2>
              <p className="text-text-secondary">
                Expert demonstrations and step-by-step installation guides
              </p>
            </div>
            <button className="flex items-center space-x-2 px-5 py-2.5 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-secondary transition-colors">
              <span>View All Videos</span>
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockVideoTutorials.map((video) =>
            <VideoTutorialCard key={video.id} {...video} />
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Webinars Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Upcoming Industry Webinars
              </h2>
              <p className="text-text-secondary">
                Join expert-led sessions on medical equipment technology and compliance
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockWebinars.map((webinar) =>
            <WebinarCard key={webinar.id} {...webinar} />
            )}
          </div>
        </div>
      </section>

      {/* Community Forum CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              Join Our Technical Community Forum
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Connect with biomedical engineers, technicians, and industry experts. Share knowledge, ask questions, and collaborate on technical challenges.
            </p>
            <button className="px-8 py-4 bg-white text-primary font-semibold rounded-md hover:bg-gray-100 transition-colors">
              Access Community Forum
            </button>
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="bg-muted rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  Need Personalized Technical Support?
                </h2>
                <p className="text-text-secondary mb-6">
                  Our team of certified biomedical engineers and technical specialists is available to provide customized guidance for your specific equipment needs.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-brand-accent flex-shrink-0 mt-0.5" />
                    <span className="text-text-primary">One-on-one technical consultations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-brand-accent flex-shrink-0 mt-0.5" />
                    <span className="text-text-primary">Custom installation planning</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-brand-accent flex-shrink-0 mt-0.5" />
                    <span className="text-text-primary">Compliance documentation assistance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-brand-accent flex-shrink-0 mt-0.5" />
                    <span className="text-text-primary">On-site training programs</span>
                  </li>
                </ul>
                <button className="px-8 py-4 bg-brand-cta text-white font-semibold rounded-md hover:bg-red-700 transition-colors">
                  Schedule Technical Consultation
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-lg border border-border">
                  <Icon name="PhoneIcon" size={32} className="text-primary mb-3" />
                  <h3 className="font-semibold text-text-primary mb-2">24/7 Support</h3>
                  <p className="text-sm text-text-secondary">Emergency technical assistance available</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-border">
                  <Icon name="EnvelopeIcon" size={32} className="text-primary mb-3" />
                  <h3 className="font-semibold text-text-primary mb-2">Email Support</h3>
                  <p className="text-sm text-text-secondary">Response within 4 business hours</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-border">
                  <Icon name="VideoCameraIcon" size={32} className="text-primary mb-3" />
                  <h3 className="font-semibold text-text-primary mb-2">Remote Assistance</h3>
                  <p className="text-sm text-text-secondary">Virtual troubleshooting sessions</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-border">
                  <Icon name="AcademicCapIcon" size={32} className="text-primary mb-3" />
                  <h3 className="font-semibold text-text-primary mb-2">Training Programs</h3>
                  <p className="text-sm text-text-secondary">Certification courses available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>);

}